import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
  Chip,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  MoneyOff,
  Savings,
} from '@mui/icons-material';

interface ForecastSummaryProps {
  data: {
    forecast_type: string;
    months: number;
    predictions: Array<{
      month: string;
      predicted_inflow: number;
      predicted_outflow: number;
      net_cashflow: number;
      confidence: number;
    }>;
    summary: {
      avg_monthly_inflow: number;
      avg_monthly_outflow: number;
      avg_net_cashflow: number;
      growth_rate: number;
      data_quality: string;
    };
  };
}

const ForecastSummary: React.FC<ForecastSummaryProps> = ({ data }) => {
  // Calculate totals from predictions
  const totals = React.useMemo(() => {
    const totalInflow = data.predictions.reduce((sum, p) => sum + p.predicted_inflow, 0);
    const totalOutflow = data.predictions.reduce((sum, p) => sum + p.predicted_outflow, 0);
    const totalNet = data.predictions.reduce((sum, p) => sum + p.net_cashflow, 0);
    const avgConfidence = data.predictions.reduce((sum, p) => sum + p.confidence, 0) / data.predictions.length;

    return {
      totalInflow,
      totalOutflow,
      totalNet,
      avgConfidence: avgConfidence * 100,
    };
  }, [data.predictions]);

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Format percentage
  const formatPercent = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const StatCard = ({ 
    title, 
    value, 
    subtitle, 
    icon, 
    color, 
    trend 
  }: { 
    title: string; 
    value: string; 
    subtitle?: string; 
    icon: React.ReactNode; 
    color: string;
    trend?: 'up' | 'down' | 'neutral';
  }) => (
    <Card sx={{ height: '100%', bgcolor: `${color}.50` }}>
      <CardContent>
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            <Box sx={{ color: `${color}.main` }}>{icon}</Box>
          </Stack>
          <Typography variant="h5" fontWeight="bold" color={`${color}.dark`}>
            {value}
          </Typography>
          {subtitle && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
              {trend && (
                <Chip
                  size="small"
                  icon={trend === 'up' ? <TrendingUp /> : trend === 'down' ? <TrendingDown /> : undefined}
                  label={trend === 'up' ? 'Growing' : trend === 'down' ? 'Declining' : 'Stable'}
                  color={trend === 'up' ? 'success' : trend === 'down' ? 'error' : 'default'}
                  sx={{ height: 20 }}
                />
              )}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Current Averages */}
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        📊 Current Performance (Last 6 Months)
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Monthly Inflow"
            value={formatCurrency(data.summary.avg_monthly_inflow)}
            subtitle="Average"
            icon={<AccountBalance />}
            color="success"
            trend="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Monthly Outflow"
            value={formatCurrency(data.summary.avg_monthly_outflow)}
            subtitle="Average"
            icon={<MoneyOff />}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Net Cashflow"
            value={formatCurrency(data.summary.avg_net_cashflow)}
            subtitle="Average"
            icon={<Savings />}
            color="primary"
            trend={data.summary.avg_net_cashflow > 0 ? 'up' : 'down'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Growth Rate"
            value={formatPercent(data.summary.growth_rate)}
            subtitle="Per month"
            icon={<TrendingUp />}
            color="info"
            trend="up"
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Forecast Totals */}
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        🔮 Forecast Totals (Next {data.months} Months)
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Inflow"
            value={formatCurrency(totals.totalInflow)}
            subtitle={`${data.months} months`}
            icon={<AccountBalance />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Outflow"
            value={formatCurrency(totals.totalOutflow)}
            subtitle={`${data.months} months`}
            icon={<MoneyOff />}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Net Position"
            value={formatCurrency(totals.totalNet)}
            subtitle={`${data.months} months`}
            icon={<Savings />}
            color="primary"
            trend={totals.totalNet > 0 ? 'up' : 'down'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  Avg Confidence
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {totals.avgConfidence.toFixed(0)}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={totals.avgConfidence}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {totals.avgConfidence >= 80 ? 'High confidence' : totals.avgConfidence >= 60 ? 'Medium confidence' : 'Low confidence'}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Month-by-Month Breakdown */}
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        📅 Month-by-Month Breakdown
      </Typography>
      <Card>
        <CardContent>
          <Stack spacing={2}>
            {data.predictions.map((pred, index) => (
              <Box key={index}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {pred.month}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${(pred.confidence * 100).toFixed(0)}% confidence`}
                    color={pred.confidence >= 0.8 ? 'success' : pred.confidence >= 0.6 ? 'warning' : 'error'}
                  />
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">
                      Inflow
                    </Typography>
                    <Typography variant="body2" color="success.main" fontWeight="bold">
                      {formatCurrency(pred.predicted_inflow)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">
                      Outflow
                    </Typography>
                    <Typography variant="body2" color="error.main" fontWeight="bold">
                      {formatCurrency(pred.predicted_outflow)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">
                      Net
                    </Typography>
                    <Typography variant="body2" color="primary.main" fontWeight="bold">
                      {formatCurrency(pred.net_cashflow)}
                    </Typography>
                  </Grid>
                </Grid>
                {index < data.predictions.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ForecastSummary;

